/**
 * How a T&C template body is meant to be rendered.
 *
 * Shared by {@link QuoteTermsTemplate} and {@link CheckInTermsTemplate}: the
 * flag means the same thing on both, and both are stored in the Dataverse
 * column `cr001_body_format`.
 *
 * ### Why this is per-row and not a switch someone flips once
 *
 * Every body written before markdown existed is plain prose, and plain prose
 * is not inert markdown. A clause beginning "- we are not liable for…" becomes
 * a bullet; "1. Payment terms" becomes a numbered list; a lone asterisk starts
 * italics that never close. These are customer-facing terms, and the check-in
 * ones are frozen into a legal acceptance record — silently reflowing them is
 * not a cosmetic risk.
 *
 * So the column is null on every pre-existing row, {@link normaliseTermsBodyFormat}
 * reads null as `'text'`, and an admin opts a template into markdown after
 * looking at the preview. New templates start as `'markdown'`, because a body
 * written today is written knowing the subset exists.
 */

/**
 * `'text'` renders verbatim (`white-space: pre-wrap` on the web, `\n` → `<br />`
 * in the Outlook HTML). `'markdown'` renders through the restricted subset in
 * `rendering/restrictedMarkdown.ts` — raw HTML in the body is escaped either way.
 */
export type TermsBodyFormat = 'text' | 'markdown';

/** Every accepted value, for validation and for the editors' toggle. */
export const TERMS_BODY_FORMATS: readonly TermsBodyFormat[] = ['text', 'markdown'];

/** What a template created today defaults to. Existing rows are unaffected. */
export const TERMS_BODY_FORMAT_DEFAULT: TermsBodyFormat = 'markdown';

/**
 * Coerce whatever Dataverse returned into a format.
 *
 * Null, empty, and anything unrecognised read as `'text'` — the conservative
 * direction. An unknown value means either a row written by a newer deployment
 * or a hand-edit; rendering it verbatim shows the author's characters, while
 * guessing markdown could restructure a legal document.
 */
export function normaliseTermsBodyFormat(
  value: string | null | undefined,
): TermsBodyFormat {
  return value === 'markdown' ? 'markdown' : 'text';
}
